import React from 'react';
import useSWRInfinite from 'swr/infinite';
import { useMatchMutate } from '../../../utils/swr/swr';
import { GetMarketingFolderItemsRequest, MarketingFolderItem, MarketingPaginatedResponse } from '../../../providers/api/dtos';
import { getMarketingFolderItems } from '../../../providers/api/marketing/marketingProvider';

export const MARKETING_FOLDER_ITEMS_RID = 'marketing-folder-items';

interface UseMarketingFolderItemsInfiniteResult {
  data: MarketingFolderItem[],
  isLoading: boolean,
  isLoadingMore: boolean,
  hasMore: boolean,
  error: Error | undefined,
  loadMore: VoidFunction,
  mutate: VoidFunction,
}

export const useMarketingFolderItemsInfinite = (req?: GetMarketingFolderItemsRequest): UseMarketingFolderItemsInfiniteResult => {
  const matchMutate = useMatchMutate();

  const getKey = (
    _: number,
    previousPage: MarketingPaginatedResponse<MarketingFolderItem> | null,
  ) => {
    if (!req?.folderId) return null;
    if (previousPage && !previousPage.continuation) return null;

    return [MARKETING_FOLDER_ITEMS_RID, req.folderId, req.itemType, previousPage?.continuation] as const;
  };

  const result = useSWRInfinite(
    getKey,
    ([, folderId, itemType, continuation]) => getMarketingFolderItems(
      {
        folderId: folderId as string,
        itemType,
      },
      { continuation },
    ),
  );

  const allItems = result.data?.flatMap((page) => page.content) ?? [];
  const lastPage = result.data?.at(-1);
  const isLoadingMore = result.isValidating && (result.data?.length ?? 0) > 0;

  const loadMore = React.useCallback(() => {
    if (lastPage?.continuation) {
      result.setSize((s) => s + 1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.continuation, result.setSize]);

  const mutate = React.useCallback(() => matchMutate(MARKETING_FOLDER_ITEMS_RID), [matchMutate]);

  return {
    data: allItems,
    isLoading: result.isLoading,
    isLoadingMore,
    hasMore: !!lastPage?.continuation,
    error: result.error,
    loadMore,
    mutate,
  };
};
